import React, { useState } from 'react';
import useAudioEngine from '../../../audio/useAudioEngine.js';
import useClipLookup from '../../Common/useClipLookup.js';
import Waveform from '../../Common/Waveform.jsx';
import formatClipTitle from '../../../utils/formatClipTitle.js';
import '../../../styles/theme.css';

const DEFAULT_HAND = [
  { id: 'sfx_footsteps_creaky_wood_01', label: 'Creaky Footsteps', duration: 3.2, peaks: [0.1, 0.4, 0.8, 0.5, 0.2] },
  { id: 'sfx_door_creak_heavy_01', label: 'Heavy Door Creak', duration: 2.1, peaks: [0.3, 0.7, 0.9, 0.4, 0.1] },
  { id: 'sfx_glass_shatter_bar_01', label: 'Glass Shatter', duration: 1.8, peaks: [0.9, 0.8, 0.6, 0.3, 0.1] },
  { id: 'sfx_gunshot_silenced_reverb_01', label: 'Silenced Gunshot', duration: 1.2, peaks: [0.8, 0.4, 0.2, 0.1] },
  { id: 'sfx_screaming_goat', label: 'Screaming Goat', duration: 2.5, peaks: [0.2, 0.6, 0.9, 0.8, 0.3] },
  { id: 'sfx_metal_pipe_falling', label: 'Metal Pipe Clatter', duration: 2.8, peaks: [0.7, 0.9, 0.8, 0.5, 0.2] }
];

export function RadioDramaBuilder({
  assignedPrompt = {},
  onSubmit = () => {},
  audioEngine = null,
  onSkipPrompt = null
}) {
  const lookupClip = useClipLookup();
  const promptText = assignedPrompt.text || assignedPrompt.prompt || 'A dramatic mystery scene';

  // 3-clip sequence slots (Slot 0, 1, 2)
  const [slots, setSlots] = useState([null, null, null]);
  // 2 configurable gap timings (between 0 and 3 seconds)
  const [gaps, setGaps] = useState([0.5, 0.5]);
  const [activeSlotIdx, setActiveSlotIdx] = useState(0);
  const [previewing, setPreviewing] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const audio = useAudioEngine('player');
  const hand = DEFAULT_HAND;

  const handleSelectClip = (clip) => {
    const newSlots = [...slots];
    newSlots[activeSlotIdx] = clip;
    setSlots(newSlots);
    setErrorMsg(null);

    // Auto-advance active slot to next empty slot
    const nextEmpty = newSlots.findIndex((s, i) => !s && i > activeSlotIdx);
    if (nextEmpty !== -1) {
      setActiveSlotIdx(nextEmpty);
    }
  };

  const handleRemoveSlot = (idx) => {
    const newSlots = [...slots];
    newSlots[idx] = null;
    setSlots(newSlots);
    setActiveSlotIdx(idx);
  };

  const handleGapChange = (gapIdx, val) => {
    const num = Math.min(3.0, Math.max(0.0, parseFloat(val) || 0));
    const newGaps = [...gaps];
    newGaps[gapIdx] = num;
    setGaps(newGaps);
  };

  const handleAuditionClip = async (clip) => {
    if (!clip) return;
    try {
      if (audioEngine) {
        await audioEngine.playClip(clip.id);
      } else if (audio.audition) {
        await audio.audition(clip);
      }
    } catch {
      // Catch audition
    }
  };

  const handlePreviewScene = async () => {
    const validClips = slots.filter(Boolean);
    if (validClips.length !== 3) {
      setErrorMsg('Please select all 3 clips before previewing.');
      return;
    }

    setPreviewing(true);
    setErrorMsg(null);

    const sequenceItems = validClips.map((c, i) => ({
      clipId: c.id,
      buffer: c.buffer,
      gap: i === 0 ? 0 : gaps[i - 1]
    }));

    try {
      if (audioEngine) {
        await audioEngine.playSequence(sequenceItems);
      } else if (audio.playSequence) {
        await audio.playSequence(sequenceItems);
      }
    } catch {
      // Catch preview
    } finally {
      const totalDur = validClips.reduce((acc, c) => acc + (c.duration || 2.0), 0) + gaps[0] + gaps[1];
      setTimeout(() => setPreviewing(false), totalDur * 1000 + 500);
    }
  };

  const handleSubmit = () => {
    const validClips = slots.filter(Boolean);
    if (validClips.length !== 3) {
      setErrorMsg('A Foley Scene requires exactly 3 clips.');
      return;
    }

    onSubmit({
      clips: validClips.map(c => c.id),
      gaps: [gaps[0], gaps[1]],
      labels: validClips.map(c => c.label)
    });
  };

  const isComplete = slots.every(Boolean);

  return (
    <div
      className="radio-drama-builder"
      data-testid="radio-drama-builder"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        maxWidth: '600px',
        margin: '0 auto',
        width: '100%',
        boxSizing: 'border-box',
        color: 'var(--sd-ink, #EDEFF2)',
        fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
      }}
    >
      {/* Assigned Scenario Header */}
      <div
        style={{
          padding: '18px 20px',
          background: 'var(--sd-panel, #0D1015)',
          border: '1px solid var(--sd-line, #1A1F25)',
          borderRadius: '16px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
          <span style={{
            fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
            fontSize: '11px',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'var(--sd-teal, #3ACFCF)',
            fontWeight: 600,
          }}>
            Scenario Assignment
          </span>
          {onSkipPrompt && (
            <button
              onClick={onSkipPrompt}
              style={{
                color: 'var(--sd-rose, #E4738A)',
                fontSize: '0.85rem',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
              }}
            >
              ⚠️ Skip Scenario
            </button>
          )}
        </div>
        <div data-testid="scenario-prompt" style={{ fontSize: '1.2rem', fontWeight: 800, color: 'var(--sd-ink, #EDEFF2)', marginTop: '4px' }}>
          "{promptText}"
        </div>
      </div>

      {/* 3-Clip Timeline Sequence & Gap Controls */}
      <div
        style={{
          padding: '20px',
          background: 'var(--sd-panel, #0D1015)',
          border: '1px solid var(--sd-line, #1A1F25)',
          borderRadius: '16px',
        }}
      >
        <div style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--sd-dim, #6E7982)', marginBottom: '16px' }}>
          🎬 Build 3-Clip Radio Drama Sequence
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {slots.map((slot, idx) => (
            <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {/* Slot Box */}
              <div
                data-testid={`foley-slot-${idx}`}
                onClick={() => setActiveSlotIdx(idx)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '14px 16px',
                  borderRadius: '12px',
                  background: activeSlotIdx === idx ? 'rgba(58, 207, 207, 0.08)' : 'var(--sd-panel-2, #0E1116)',
                  border: activeSlotIdx === idx
                    ? '1px solid var(--sd-teal, #3ACFCF)'
                    : (slot ? '1px solid var(--sd-line-2, #1C2229)' : '1px dashed var(--sd-line-3, #1F252C)'),
                  cursor: 'pointer',
                  minHeight: '52px',
                  transition: 'all 0.15s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: '1 1 0', minWidth: 0 }}>
                  <span
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: slot ? 'var(--sd-teal, #3ACFCF)' : 'var(--sd-panel, #0D1015)',
                      color: slot ? 'var(--sd-ground, #08090B)' : 'var(--sd-dim, #6E7982)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 800,
                      fontSize: '0.85rem',
                      fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                      flexShrink: 0,
                    }}
                  >
                    {idx + 1}
                  </span>

                  <div style={{ flex: '1 1 0', minWidth: 0 }}>
                    {slot ? (
                      <div
                        title={formatClipTitle(slot, 100)}
                        style={{
                          fontWeight: 700,
                          color: 'var(--sd-ink, #EDEFF2)',
                          fontSize: '1rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {formatClipTitle(slot)}
                      </div>
                    ) : (
                      <div style={{ color: 'var(--sd-dim, #6E7982)', fontSize: '0.9rem', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {activeSlotIdx === idx ? 'Select clip below...' : 'Empty Slot — Click to choose'}
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {slot && (
                    <>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleAuditionClip(slot); }}
                        title="Audition Sound"
                        style={{
                          background: 'var(--sd-panel, #0D1015)',
                          border: '1px solid var(--sd-line, #1A1F25)',
                          borderRadius: '8px',
                          color: 'var(--sd-ink-2, #C6CDD4)',
                          cursor: 'pointer',
                          padding: '6px 10px',
                          fontSize: '0.85rem',
                          minHeight: '36px',
                        }}
                      >
                        🔊
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleRemoveSlot(idx); }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'var(--sd-rose, #E4738A)',
                          cursor: 'pointer',
                          padding: '6px',
                          fontSize: '1rem',
                        }}
                      >
                        ✕
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Custom Gap Control between Slot 1 & 2, and Slot 2 & 3 */}
              {idx < 2 && (
                <div
                  data-testid={`gap-control-${idx}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                    padding: '8px 16px',
                    background: 'var(--sd-panel-2, #0E1116)',
                    borderRadius: '10px',
                    border: '1px solid var(--sd-line, #1A1F25)',
                    margin: '2px 8px'
                  }}
                >
                  <span style={{
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: 'var(--sd-teal, #3ACFCF)',
                    fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                  }}>
                    ⏱️ Gap {idx + 1}:
                  </span>

                  <input
                    type="range"
                    min="0"
                    max="3"
                    step="0.5"
                    value={gaps[idx]}
                    onChange={(e) => handleGapChange(idx, e.target.value)}
                    style={{ flex: 1, accentColor: 'var(--sd-teal, #3ACFCF)', cursor: 'pointer' }}
                    data-testid={`gap-slider-${idx}`}
                  />

                  <span
                    data-testid={`gap-value-${idx}`}
                    style={{
                      fontSize: '0.9rem',
                      fontWeight: 700,
                      color: 'var(--sd-ink, #EDEFF2)',
                      minWidth: '36px',
                      textAlign: 'right',
                      fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                    }}
                  >
                    {gaps[idx].toFixed(1)}s
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {errorMsg && (
        <div data-testid="builder-error" style={{ color: 'var(--sd-rose, #E4738A)', fontSize: '0.9rem', fontWeight: 700, textAlign: 'center' }}>
          {errorMsg}
        </div>
      )}

      {/* Hand of Clips Picker */}
      <div
        style={{
          padding: '18px 20px',
          background: 'var(--sd-panel, #0D1015)',
          border: '1px solid var(--sd-line, #1A1F25)',
          borderRadius: '16px',
        }}
      >
        <div style={{
          fontSize: '0.9rem',
          fontWeight: 700,
          color: 'var(--sd-dim, #6E7982)',
          marginBottom: '12px',
          fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
        }}>
          🔊 Clip Hand (Selecting for Slot {activeSlotIdx + 1}):
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
            gap: '10px',
            maxHeight: '220px',
            overflowY: 'auto',
            paddingRight: '4px'
          }}
        >
          {hand.map((clip) => {
            const isSelectedInAnySlot = slots.some(s => s && (s.id === clip.id || s.clipId === clip.id));
            return (
              <div
                key={clip.id}
                onClick={() => handleSelectClip(clip)}
                style={{
                  background: isSelectedInAnySlot ? 'rgba(58, 207, 207, 0.10)' : 'var(--sd-panel-2, #0E1116)',
                  border: isSelectedInAnySlot ? '1px solid var(--sd-teal, #3ACFCF)' : '1px solid var(--sd-line-2, #1C2229)',
                  borderRadius: '12px',
                  padding: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                  transition: 'all 0.15s ease'
                }}
              >
                <div
                  title={formatClipTitle(clip, 100)}
                  style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--sd-ink, #EDEFF2)', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', minWidth: 0 }}
                >
                  {formatClipTitle(clip)}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{
                    fontSize: '0.75rem',
                    color: 'var(--sd-dim, #6E7982)',
                    fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                  }}>
                    {clip.duration ? `${clip.duration}s` : 'SFX'}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleAuditionClip(clip); }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--sd-teal, #3ACFCF)',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      padding: '4px',
                    }}
                    title="Audition"
                  >
                    ▶
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Action Buttons: Preview & Submit */}
      <div style={{ display: 'flex', gap: '12px', marginTop: '4px' }}>
        <button
          style={{
            flex: 1,
            height: '52px',
            minHeight: '44px',
            borderRadius: '14px',
            background: 'var(--sd-panel-2, #0E1116)',
            border: '1px solid var(--sd-line-2, #1C2229)',
            color: 'var(--sd-ink, #EDEFF2)',
            fontWeight: 700,
            fontSize: '14px',
            cursor: !isComplete || previewing ? 'not-allowed' : 'pointer',
            opacity: !isComplete || previewing ? 0.6 : 1,
            fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
          }}
          disabled={!isComplete || previewing}
          onClick={handlePreviewScene}
          data-testid="preview-scene-btn"
        >
          {previewing ? '🔊 Playing Preview...' : '▶ Preview Scene'}
        </button>

        <button
          style={{
            flex: 1.5,
            height: '52px',
            minHeight: '44px',
            borderRadius: '14px',
            background: isComplete ? 'var(--sd-teal, #3ACFCF)' : 'var(--sd-panel-2, #0E1116)',
            border: isComplete ? '1px solid var(--sd-teal, #3ACFCF)' : '1px solid var(--sd-line, #1A1F25)',
            color: isComplete ? 'var(--sd-ground, #08090B)' : 'var(--sd-dim, #6E7982)',
            fontWeight: 700,
            fontSize: '14px',
            cursor: !isComplete ? 'not-allowed' : 'pointer',
            fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
            transition: 'all 0.15s ease',
          }}
          disabled={!isComplete}
          onClick={handleSubmit}
          data-testid="submit-scene-btn"
        >
          🚀 Submit 3-Clip Scene
        </button>
      </div>
    </div>
  );
}

export default RadioDramaBuilder;
